import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { isSdkAuthenticatedAndUserLoaded } from '@boldpenguin/sdk-redux';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { BpSdkStoreService } from '../../services/bp-sdk-store-service/bp-sdk-store.service';
import {
  MixpanelEventType,
  MixpanelService,
} from '../../services/mixpanel-service/mixpanel.service';
import { addRedirect } from '../../store/redirect/redirect.actions';

export const authenticatedGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const bpSdkStoreService = inject(BpSdkStoreService);
  const mixpanelService = inject(MixpanelService);
  const store = inject(Store);
  const router = inject(Router);

  return bpSdkStoreService.isAuthenticated$.pipe(
    // When the userinfo endpoint returns a non-success status code that is not a 401 (500, 502, etc),
    // SDK will set an error status for auth, but not logout the user.
    // Here we grab the sdk authentication and user status, which ensures the request was successful.
    withLatestFrom(
      bpSdkStoreService.state$.pipe(select(isSdkAuthenticatedAndUserLoaded)),
    ),
    map(([isAuthenticated, isSdkAuthed]) => isAuthenticated && isSdkAuthed),
    tap((isAuthenticated) => {
      if (!isAuthenticated) {
        mixpanelService.track(MixpanelEventType.REDIRECTED_TO_LOGIN);
        store.dispatch(addRedirect({ path: state.url }));
      }
    }),
    map(
      (isAuthenticated) =>
        isAuthenticated ||
        router.createUrlTree(['auth', 'login'], {
          queryParams: { ...route.params },
        }),
    ),
  );
};
